import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import AddIcon from "@material-ui/icons/Add";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button, Typography } from "@material-ui/core";

import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import FoodDayTypeDictionary from "stores/dictionary/FoodDayTypeDictionary";
import FoodMealTypeDictionary from "stores/dictionary/FoodMealTypeDictionary";
import InAppNutritionStore from "stores/InAppNutritionStore";
import NutritionCategoryDetailsStore from "stores/NutritionCategoryDetailsStore";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import Placeholder from "components/Placeholder";
import PublishStatus from "components/PublishStatus";
import Dropdown from "components/Input/Dropdown";
import FilteringControl from "components/FilteringControl";
import Popup from "components/Popup";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";

import AccessForm from "./AccessForm";
import CategoryDetailsCard from "./CategoryDetailsCard";
import RecipeForm from "./RecipeForm";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { getDayOfWeek, getIntervalText, getMonthWithDay } from "utils/date";
import { areStatusActiveProgramFiltersEmpty } from "utils/filter";
import { getConfirmRemovalText } from "utils/removeItem";

import {
  DictionaryItem,
  PROGRAM_PERMISSION_TYPES,
  PUBLISH_STATUSES,
} from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";

import ROUTES from "navigation/routes";

import {
  CategoryCardWrapper,
  BackButtonWrapper,
  SearchContainer,
  Search,
  SearchControls,
  SearchControlWrapper,
  ScrollLoader,
  WeekCategoryPageSubtitle,
  WeekCategoryContainer,
  WeekCategoryHeader,
  Wrapper,
  OverflowLoader,
  Divider,
  PublishStatusWrapper,
  HeaderControlsContainer,
  HeaderControl,
  MealTypeWrapper,
  MealTypeList,
} from "./styles";

type Props = GlobalPopupProps &
  SnackbarContextProps &
  RouteComponentProps<{ id: string }> & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class NutritionCategoryDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable managingDayNumber: number | null = null;
  @observable newDayType: number | undefined = undefined;
  @observable previousDayType: number | undefined = undefined;
  @observable updateConfirmationIsOpened: boolean = false;

  get categoryId() {
    const { id } = this.props.match.params;

    return id ? Number.parseInt(id) : null;
  }

  @computed get category() {
    return InAppNutritionStore.categoryDetails;
  }

  @computed
  get isWeekCategory() {
    return this.category?.isWeekCategory;
  }

  @computed get isSixWeekCategory() {
    return this.category?.isSixWeekCategory;
  }

  @computed get weekText() {
    return this.category?.weekText;
  }

  @computed get weekCategoryDays() {
    return this.category?.weekCategoryDays;
  }

  @computed get weekCategoryRecipes() {
    return this.weekCategoryDays?.map((day: Date, index: number) => ({
      day,
      dayNumber: index + 1,
      mealTypeRecipes: FoodMealTypeDictionary.items
        .map(({ id }) => ({
          foodMealTypeId: id,
          recipes: this.recipes.filter(
            ({ day, foodMealType }: { day: number; foodMealType: any }) =>
              day === index + 1 && foodMealType.id === id,
          ),
        }))
        .filter(({ recipes }) => !!recipes.length),
    }));
  }

  @computed get categoryTitle() {
    return this.category?.title;
  }

  @computed get categoryPublishStatus() {
    return this.category?.publishStatus;
  }

  @computed get shouldShowUpdateConfirmation() {
    return (
      this.categoryPublishStatus &&
      [
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
        PUBLISH_STATUSES.PUBLISHED,
      ].includes(this.categoryPublishStatus)
    );
  }

  @computed get recipes() {
    return NutritionCategoryDetailsStore.items;
  }

  @computed get recipeListIsEmpty() {
    return this.recipes.length === 0;
  }

  @computed get isFiltersEmpty() {
    const { filters } = NutritionCategoryDetailsStore;

    return areStatusActiveProgramFiltersEmpty(filters);
  }

  async componentDidMount() {
    if (this.categoryId) {
      NutritionCategoryDetailsStore.applyFilter({
        column: "FoodCategoryId",
        value: this.categoryId,
      });
      await Promise.all([
        InAppNutritionStore.fetchCategoryDetails(this.categoryId),
        NutritionCategoryDetailsStore.fetch(),
        FoodDayTypeDictionary.fetch(),
        FoodMealTypeDictionary.fetch(),
        ActiveProgramDictionary.fetch(),
      ]);

      if (this.isWeekCategory) {
        await NutritionCategoryDetailsStore.fetchAllRecipes();
      }
    }
  }

  componentWillUnmount() {
    NutritionCategoryDetailsStore.reset();
    InAppNutritionStore.resetCategoryDetails();
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onBackClick = () => {
    this.props.history.push(ROUTES.IN_APP_NUTRITION);
  };

  fetchMoreRecipes = async () => {
    const { setMessage } = this.props;

    try {
      await NutritionCategoryDetailsStore.fetchMoreRecipes();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    NutritionCategoryDetailsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      NutritionCategoryDetailsStore.fetch();
  };

  onSearchClear = () => {
    NutritionCategoryDetailsStore.clearSearch();
    NutritionCategoryDetailsStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      NutritionCategoryDetailsStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    NutritionCategoryDetailsStore.fetch();
  };

  onFilterClear = () => {
    NutritionCategoryDetailsStore.clearFilter(FILTER_COLUMNS.ACTIVE_PROGRAMS);
    NutritionCategoryDetailsStore.clearFilter(FILTER_COLUMNS.PUBLISH_STATUS);
    NutritionCategoryDetailsStore.fetch();
  };

  onAddRecipe = (dayRecipes?: {
    day: Date;
    dayNumber: number;
    mealTypeRecipes: any[];
  }) => () => {
    const { openPopup } = this.props;

    const existingWeekrecipes = dayRecipes?.mealTypeRecipes.map(
      ({ foodMealTypeId, recipes }) => ({
        foodMealTypeId,
        recipeIds: recipes.map(({ id }: { id: number }) => id),
      }),
    );

    openPopup({
      key: "AddRecipe",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <RecipeForm
          isLoading={false}
          onSubmit={this.onAddRecipeSubmit(close, dayRecipes?.dayNumber)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          categoryId={this.categoryId}
          isWeekCategory={this.isWeekCategory}
          existingWeekRecipes={existingWeekrecipes}
          shouldShowUpdateConfirmation={this.shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onAddRecipeSubmit = (close: () => void, day?: number) => async (
    values: any,
  ) => {
    const { setMessage } = this.props;
    const {
      recipes,
      customLabel,
      recipeLabel,
      updateDateTime,
      ...restValues
    } = values;

    const payload = this.isWeekCategory
      ? {
          dayOfWeek: day ?? 0,
          recipes: Object.keys(restValues)
            .map((key: string) => {
              const recipeType = FoodMealTypeDictionary.options.find(
                ({ label }) => label === key,
              );

              return restValues[key]?.map(({ id }: { id: number }) => ({
                recipeId: id,
                foodMealTypeId: recipeType?.value,
              }));
            })
            .flat(),
          categoryId: this.categoryId,
        }
      : {
          recipes: recipes?.map(({ id }: { id: number }) => ({
            recipeId: id,
          })),
          categoryId: this.categoryId,
        };

    if (!payload.recipes || payload.recipes?.length === 0) {
      this.resetIsPopupDirty();
      close();
      return;
    }

    try {
      await NutritionCategoryDetailsStore.addRecipeToCategory(payload);
      this.resetIsPopupDirty();
      close();
      NutritionCategoryDetailsStore.resetPage();
      NutritionCategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onManageAccessPrograms = (recipe: any) => () => {
    const { openPopup } = this.props;

    const shouldShowUpdateConfirmation =
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(recipe.publishStatus) && this.shouldShowUpdateConfirmation;

    openPopup({
      key: "ManageCategory",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <AccessForm
          isLoading={NutritionCategoryDetailsStore.isDetailsManaging}
          isFormLoading={NutritionCategoryDetailsStore.isRecipeLoading}
          initialValues={{ categoryId: this.categoryId, recipeId: recipe.id }}
          onSubmit={this.onManageAccessProgramsSubmit(recipe, close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          shouldShowUpdateConfirmation={shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onManageAccessProgramsSubmit = (recipe: any, close: () => void) => async (
    values: any,
  ) => {
    const { setMessage } = this.props;
    const { activePrograms } = values;

    const payload = {
      categoryId: this.categoryId,
      recipeId: recipe.id,
      activePrograms: activePrograms.map(
        ({ id, switchState }: { id: number; switchState: boolean }) => ({
          activeProgramId: id,
          permission: switchState
            ? PROGRAM_PERMISSION_TYPES.PREVIEW
            : PROGRAM_PERMISSION_TYPES.FULL_ACCESS,
        }),
      ),
    };

    try {
      await NutritionCategoryDetailsStore.updateRecipeAccessPrograms(payload);
      this.resetIsPopupDirty();
      close();
      NutritionCategoryDetailsStore.resetPage();
      NutritionCategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onRemoveRecipe = (recipe: any, dayNumber?: number) => () => {
    const { openPopup } = this.props;

    const text = getConfirmRemovalText({
      entity: recipe,
      suffix: "recipe",
      category: this.category,
    });

    openPopup({
      key: "RemoveRecipe",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Remove Recipe"
          text={text}
          declineText="Cancel"
          acceptText="Remove"
          onDecline={close}
          onAccept={this.onRemoveRecipeSubmit(close, recipe, dayNumber)}
          isLoading={NutritionCategoryDetailsStore.isDetailsManaging}
        />
      ),
    });
  };

  onRemoveRecipeSubmit = (
    close: () => void,
    recipe: any,
    day?: number,
  ) => async () => {
    const { setMessage } = this.props;

    const payload = this.isWeekCategory
      ? {
          dayOfWeek: day ?? 0,
          recipesIds: [recipe?.id],
          categoryId: this.categoryId,
        }
      : { recipesIds: [recipe?.id], categoryId: this.categoryId };

    try {
      await NutritionCategoryDetailsStore.removeRecipeFromCategory(payload);
      close();
      NutritionCategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDetailsClick = (recipe: any) => () => {
    const path = generatePath(
      ROUTES.IN_APP_NUTRITION_CATEGORY_DETAILS_RECIPE_DETAILS,
      { id: this.categoryId, recipeId: recipe.id },
    );
    this.props.history.push({ pathname: path, state: { readOnly: true } });
  };

  onDragEnd = (dayOfWeek?: number, foodMealTypeId?: number) => async (
    result: DropResult,
  ) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      this.categoryId &&
        (await NutritionCategoryDetailsStore.reorderRecipes({
          categoryId: this.categoryId,
          startIndex,
          endIndex,
          dayOfWeek,
          foodMealTypeId,
        }));
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  getDayType = (dayRecipes: {
    day: Date;
    dayNumber: number;
    mealTypeRecipes: any[];
  }) => {
    return this.category?.foodCategoryDays.find(
      ({ dayOfWeek }: { dayOfWeek: number }) =>
        dayOfWeek === dayRecipes.dayNumber,
    )?.foodDayType?.id;
  };

  onChangeDayType = (dayRecipes?: {
    day: Date;
    dayNumber: number;
    mealTypeRecipes: any[];
  }) => (item: DictionaryItem) => {
    this.newDayType = item.value;
    this.previousDayType = this.getDayType(dayRecipes);
    this.managingDayNumber = dayRecipes?.dayNumber ?? null;

    if (this.shouldShowUpdateConfirmation) {
      this.openUpdateConfirmation();
    } else {
      this.confirmDayUpdate();
    }
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
    this.newDayType = undefined;
    this.previousDayType = undefined;
    this.managingDayNumber = null;
  };

  confirmDayUpdate = async () => {
    const { setMessage } = this.props;

    const payload = {
      categoryId: this.categoryId,
      dayOfWeek: this.managingDayNumber,
      foodDayTypeId: this.newDayType,
    };

    try {
      await InAppNutritionStore.updateDayType(payload);
      this.closeUpdateConfirmation();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                Back to categories
              </Button>
            </BackButtonWrapper>
          }
          title={this.categoryTitle}
          leftControls={
            this.categoryPublishStatus && (
              <PublishStatusWrapper>
                <PublishStatus status={this.categoryPublishStatus} />
              </PublishStatusWrapper>
            )
          }
          controls={
            !this.isWeekCategory && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddRecipe()}
              >
                Add Recipe
              </Button>
            )
          }
          isLoading={
            InAppNutritionStore.isCategoryLoading ||
            NutritionCategoryDetailsStore.isLoading
          }
        >
          {this.isWeekCategory ? (
            <>
              {NutritionCategoryDetailsStore.isRecipeReordering && (
                <OverflowLoader />
              )}
              {this.updateConfirmationIsOpened && (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmDayUpdate}
                    onDecline={this.closeUpdateConfirmation}
                    isLoading={InAppNutritionStore.isCategoryManaging}
                  />
                </Popup>
              )}
              <WeekCategoryPageSubtitle>
                <Typography variant="h2" color="textPrimary">
                  {!this.isSixWeekCategory
                    ? this.weekCategoryDays &&
                      getIntervalText(this.weekCategoryDays)
                    : this.weekText}
                </Typography>
              </WeekCategoryPageSubtitle>
              {this.weekCategoryRecipes?.map(
                (dayRecipes: {
                  day: Date;
                  dayNumber: number;
                  mealTypeRecipes: any[];
                }) => (
                  <WeekCategoryContainer key={dayRecipes.day.toString()}>
                    <WeekCategoryHeader>
                      <Wrapper>
                        <Typography variant="h3" color="textPrimary">
                          {getDayOfWeek(dayRecipes.day)}
                        </Typography>
                        {!this.isSixWeekCategory && (
                          <Typography variant="subtitle1" color="secondary">
                            {getMonthWithDay(dayRecipes.day)}
                          </Typography>
                        )}
                      </Wrapper>
                      <Wrapper>
                        <HeaderControlsContainer>
                          <HeaderControl isDropdown>
                            <Dropdown
                              onChange={this.onChangeDayType(dayRecipes)}
                              input={{
                                value: this.getDayType(dayRecipes),
                              }}
                              placeholder={"Select day type"}
                              loading={
                                FoodDayTypeDictionary.isLoading ||
                                (InAppNutritionStore.isCategoryManaging &&
                                  this.managingDayNumber ===
                                    dayRecipes.dayNumber)
                              }
                              options={FoodDayTypeDictionary.options}
                              disabled={
                                InAppNutritionStore.isCategoryManaging &&
                                this.managingDayNumber !== dayRecipes.dayNumber
                              }
                            />
                          </HeaderControl>
                          <HeaderControl>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={this.onAddRecipe(dayRecipes)}
                            >
                              Add Recipe
                            </Button>
                          </HeaderControl>
                        </HeaderControlsContainer>
                      </Wrapper>
                    </WeekCategoryHeader>
                    <Divider />
                    {dayRecipes.mealTypeRecipes.length ? (
                      dayRecipes.mealTypeRecipes.map(
                        ({
                          foodMealTypeId,
                          recipes,
                        }: {
                          foodMealTypeId: number;
                          recipes: any[];
                        }) => (
                          <MealTypeWrapper
                            key={`day:${dayRecipes.dayNumber}_foodMealTypeId:${foodMealTypeId}`}
                          >
                            {recipes.length === 1 ? (
                              <CategoryCardWrapper>
                                <CategoryDetailsCard
                                  title={`${recipes[0].foodMealType?.name}: ${recipes[0].title}`}
                                  duration={recipes[0].durationText}
                                  image={recipes[0].imageUrl}
                                  publishStatus={recipes[0].publishStatus}
                                  onDetailsClick={this.onDetailsClick(
                                    recipes[0],
                                  )}
                                  onRemove={this.onRemoveRecipe(
                                    recipes[0],
                                    dayRecipes.dayNumber,
                                  )}
                                />
                              </CategoryCardWrapper>
                            ) : (
                              <DragDropContext
                                onDragEnd={this.onDragEnd(
                                  dayRecipes.dayNumber,
                                  foodMealTypeId,
                                )}
                              >
                                <Droppable droppableId="droppable">
                                  {(provided, snapshot) => (
                                    <MealTypeList
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      isDraggingOver={snapshot.isDraggingOver}
                                    >
                                      {recipes.map((recipe, index) => (
                                        <Draggable
                                          key={`id:${recipe.id}_created:${recipe.created}`}
                                          draggableId={recipe.id.toString()}
                                          index={index}
                                        >
                                          {(provided, snapshot) => (
                                            <CategoryCardWrapper
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                            >
                                              <CategoryDetailsCard
                                                title={`${recipe.foodMealType?.name}: ${recipe.title}`}
                                                duration={recipe.durationText}
                                                image={recipe.imageUrl}
                                                publishStatus={
                                                  recipe.publishStatus
                                                }
                                                onDetailsClick={this.onDetailsClick(
                                                  recipe,
                                                )}
                                                onRemove={this.onRemoveRecipe(
                                                  recipe,
                                                  dayRecipes.dayNumber,
                                                )}
                                                isDragging={snapshot.isDragging}
                                                dragHandleProps={
                                                  provided.dragHandleProps
                                                }
                                              />
                                            </CategoryCardWrapper>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </MealTypeList>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                          </MealTypeWrapper>
                        ),
                      )
                    ) : (
                      <Placeholder
                        height={132}
                        width={132}
                        text={"No recipes added yet."}
                      />
                    )}
                  </WeekCategoryContainer>
                ),
              )}
            </>
          ) : (
            <>
              <SearchContainer>
                <Search
                  initValue={NutritionCategoryDetailsStore.search}
                  onApply={this.onSearchApply}
                  onClear={this.onSearchClear}
                />
                <SearchControls>
                  <SearchControlWrapper>
                    <FilteringControl
                      initValue={NutritionCategoryDetailsStore.filters}
                      onApply={this.onFilterApply}
                      onClear={this.onFilterClear}
                      statusFiltering
                      accessProgramsFiltering
                    />
                  </SearchControlWrapper>
                </SearchControls>
              </SearchContainer>

              {!this.recipeListIsEmpty ? (
                <>
                  {NutritionCategoryDetailsStore.isRecipeReordering && (
                    <OverflowLoader />
                  )}
                  <InfiniteScroll
                    dataLength={this.recipes.length}
                    hasMore={NutritionCategoryDetailsStore.hasMoreInfo}
                    next={this.fetchMoreRecipes}
                    height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
                    loader={<ScrollLoader />}
                  >
                    <DragDropContext onDragEnd={this.onDragEnd()}>
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {this.recipes.map((recipe, index) => (
                              <Draggable
                                key={recipe.id}
                                draggableId={recipe.id.toString()}
                                index={index}
                                isDragDisabled={!this.isFiltersEmpty}
                              >
                                {(provided, snapshot) => (
                                  <CategoryCardWrapper
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <CategoryDetailsCard
                                      title={recipe.title}
                                      duration={recipe.durationText}
                                      image={recipe.imageUrl}
                                      publishStatus={recipe.publishStatus}
                                      onDetailsClick={this.onDetailsClick(
                                        recipe,
                                      )}
                                      onAccessClick={this.onManageAccessPrograms(
                                        recipe,
                                      )}
                                      onRemove={this.onRemoveRecipe(recipe)}
                                      isDragging={snapshot.isDragging}
                                      dragHandleProps={provided.dragHandleProps}
                                    />
                                  </CategoryCardWrapper>
                                )}
                              </Draggable>
                            ))}

                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </InfiniteScroll>
                </>
              ) : (
                <Placeholder
                  height={280}
                  width={280}
                  text={
                    NutritionCategoryDetailsStore.search
                      ? "No matches found. Please try another search."
                      : "No recipes added yet."
                  }
                />
              )}
            </>
          )}
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(
  withGlobalSnackbar(withRouter(NutritionCategoryDetails)),
);
